import { useEffect, useState } from "react";
import Title from "../../components/common/title/Title";
import { deleteApi, getApi, postApi, putApi } from "../../services/api/globalApi";
import { errorMessage, successMessage, warningMessage } from "../../utils/toastify-message/ToastifyMessage";
import { useLocation, useNavigate } from "react-router-dom";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import Swal from "sweetalert2";

const roleOptions = [
  { value: "customer", label: "مشتری" },
  { value: "affiliate", label: "نماینده" },
  { value: "seller", label: "فروشنده" },
  { value: "vendoreOwner", label: "مالک فروشگاه" },
  { value: "admin", label: "ادمین" },
];
const fieldClass = "w-full rounded-lg border border-gray-300 bg-white p-2 text-gray-900 dark:bg-gray-800 dark:text-white";
const labelClass = "block mb-2 text-sm font-medium text-gray-700 dark:text-gray-200";
const hintClass = "mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400";

function Info({ children }) {
  return <p className={hintClass}><span aria-hidden="true" className="ml-1 text-blue-600">ⓘ</span>{children}</p>;
}

const getApiError = (error, fallback) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.errors?.[0]?.message;
  if (status >= 500) return "سرور خطا داد. کمی بعد دوباره تلاش کنید.";
  if (typeof message === "string" && message.trim()) return message;
  if (status === 404 || status === 405) return "مسیر ذخیره در سرور پیدا نشد. نسخه سرور را به‌روز کنید.";
  if (status === 409) return "اطلاعات تغییر کرده است. صفحه را تازه کنید و دوباره تلاش کنید.";
  if (status === 422) return "یکی از فیلدها درست نیست. اطلاعات کمپین را بررسی کنید.";
  if (!error?.response) return "ارتباط با سرور برقرار نشد. اتصال را بررسی کنید.";
  return fallback;
};

const validateCampaign = (campaign) => {
  if (!campaign.name?.trim()) return "نام کمپین را وارد کنید.";
  if (!campaign.startsAt || !campaign.endsAt) return "تاریخ شروع و پایان را انتخاب کنید.";
  if (!Number.isFinite(Date.parse(campaign.startsAt)) || !Number.isFinite(Date.parse(campaign.endsAt))) return "تاریخ کمپین درست نیست.";
  if (new Date(campaign.startsAt) >= new Date(campaign.endsAt)) return "پایان کمپین باید بعد از شروع آن باشد.";
  if (!campaign.roles.length) return "حداقل یک نقش انتخاب کنید.";
  const minimum = Number(campaign.minimumOrderPrice);
  if (campaign.minimumOrderPrice === "" || !Number.isSafeInteger(minimum) || minimum < 0) return "حداقل مبلغ خرید را درست وارد کنید.";
  const discount = Number(campaign.discountValue);
  if (campaign.discountValue === "" || !Number.isFinite(discount) || discount <= 0 ||
    (campaign.discountType === "percent" && discount > 100) ||
    (campaign.discountType === "amount" && !Number.isSafeInteger(discount))) {
    return campaign.discountType === "percent" ? "درصد تخفیف باید بیشتر از ۰ و حداکثر ۱۰۰ باشد." : "مبلغ تخفیف را درست وارد کنید.";
  }
  return "";
};

const formatCampaignDate = (value) => value
  ? new Intl.DateTimeFormat("fa-IR-u-ca-persian", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value))
  : "زمان تعیین نشده";

function CampaignDateTime({ label, value, onChange }) {
  return <div>
    <label className={labelClass}>{label}</label>
    <DatePicker
      value={value ? new Date(value) : null}
      onChange={(date) => onChange(date ? date.toDate().toISOString() : "")}
      calendar={persian}
      locale={persian_fa}
      format="YYYY/MM/DD HH:mm"
      calendarPosition="bottom-right"
      inputClass={fieldClass}
      containerClassName="w-full"
      placeholder="تاریخ و ساعت را انتخاب کنید"
      plugins={[<TimePicker position="bottom" hideSeconds />]}
    />
    <Info>{label.startsWith("شروع") ? "تخفیف از این زمان شروع می‌شود. ساعت بر اساس دستگاه شماست." : "تخفیف در این زمان تمام می‌شود."}</Info>
  </div>;
}

const newCampaign = () => ({
  id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name: "کمپین جدید",
  isActive: false,
  startsAt: "",
  endsAt: "",
  roles: [],
  users: [],
  minimumOrderPrice: "",
  productPriceBasis: "after",
  affiliatePriceBasis: "after",
  discountType: "percent",
  discountValue: "",
  affiliateDiscountOrder: "after",
});

export default function Campaigns() {
  const location = useLocation();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [version, setVersion] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [usersInfo, setUsersInfo] = useState({});
  const [userSearch, setUserSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [dirtyIds, setDirtyIds] = useState([]);
  const [persistedIds, setPersistedIds] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const selected = campaigns.find((item) => item.id === selectedId);
  const listedCampaigns = [...campaigns].sort((a, b) =>
    Number(b.isActive) - Number(a.isActive) ||
    (a.startsAt && b.startsAt ? new Date(a.startsAt) - new Date(b.startsAt) : a.startsAt ? -1 : b.startsAt ? 1 : 0)
  );

  useEffect(() => {
    getApi({ path: "admin/campaigns" })
      .then(({ data }) => {
        const result = data?.result;
        setCampaigns(result?.campaigns || []);
        setPersistedIds((result?.campaigns || []).map((item) => item.id));
        setVersion(result?.version || 0);
        const requestedId = new URLSearchParams(window.location.search).get("campaign");
        setSelectedId(result?.campaigns?.some((item) => item.id === requestedId)
          ? requestedId : result?.campaigns?.[0]?.id || null);
        setUsersInfo(Object.fromEntries((result?.usersInfo || []).map((user) => [user._id, user])));
      })
      .catch(() => {
        setLoadFailed(true);
        errorMessage("دریافت کمپین‌ها ناموفق بود");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const requestedId = new URLSearchParams(location.search).get("campaign");
    if (requestedId && requestedId !== selectedId && campaigns.some((item) => item.id === requestedId)) {
      setSelectedId(requestedId);
      setFormError("");
    }
  }, [location.search, campaigns, selectedId]);

  const selectCampaign = (id) => {
    setSelectedId(id);
    setFormError("");
    navigate(id ? `/campaigns?campaign=${encodeURIComponent(id)}` : "/campaigns");
  };

  const update = (changes) => {
    setCampaigns((list) => list.map((item) => item.id === selectedId ? { ...item, ...changes } : item));
    setDirtyIds((ids) => ids.includes(selectedId) ? ids : [...ids, selectedId]);
    setFormError("");
  };

  const add = () => {
    const campaign = newCampaign();
    setCampaigns((list) => [...list, campaign]);
    setDirtyIds((ids) => [...ids, campaign.id]);
    selectCampaign(campaign.id);
  };

  const remove = async (campaign) => {
    if (busyId) return;
    const isPersisted = persistedIds.includes(campaign.id);
    const confirmation = await Swal.fire({
      title: "کمپین حذف شود؟",
      text: isPersisted
        ? `«${campaign.name}» همین حالا حذف می‌شود.`
        : `پیش‌نویس «${campaign.name}» حذف می‌شود.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "حذف",
      cancelButtonText: "انصراف",
      confirmButtonColor: "#dc2626",
    });
    if (!confirmation.isConfirmed) return;
    if (isPersisted) {
      setBusyId(campaign.id);
      try {
        const response = await deleteApi({ path: `admin/campaigns/${encodeURIComponent(campaign.id)}?version=${version}` });
        const result = response?.data?.result;
        if (result?.deletedId !== campaign.id || !Number.isInteger(result?.version)) {
          throw new Error("پاسخ حذف کمپین معتبر نیست");
        }
        setVersion(result.version);
        setPersistedIds((ids) => ids.filter((id) => id !== campaign.id));
        window.dispatchEvent(new Event("campaigns-updated"));
        successMessage("کمپین حذف شد");
      } catch (error) {
        errorMessage(getApiError(error, "حذف کمپین انجام نشد."));
        setBusyId(null);
        return;
      }
      setBusyId(null);
    }
    const remaining = campaigns.filter((item) => item.id !== campaign.id);
    setCampaigns(remaining);
    setDirtyIds((ids) => ids.filter((id) => id !== campaign.id));
    if (selectedId === campaign.id) selectCampaign(remaining[0]?.id || null);
  };

  const toggleRole = (role) => {
    const roles = selected.roles.includes(role)
      ? selected.roles.filter((item) => item !== role)
      : [...selected.roles, role];
    update({ roles, users: selected.users.filter((id) => roles.includes(usersInfo[id]?.role)) });
  };

  useEffect(() => {
    const query = userSearch.trim();
    setSearchResults([]);
    setSearchError("");
    setSearching(false);
    if (!query) return;
    if (query.length > 80) {
      setSearchError("عبارت جستجو نباید بیشتر از ۸۰ نویسه باشد.");
      return;
    }
    try {
      new RegExp(query);
    } catch (_error) {
      setSearchError("الگوی جستجو معتبر نیست.");
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timeout = setTimeout(async () => {
      try {
        const response = await getApi({ path: "admin/users", params: { searchText: query, page: 1, limit: 50 } });
        if (!cancelled) setSearchResults(response?.data?.result?.data || []);
      } catch (_error) {
        if (!cancelled) setSearchError("جستجوی کاربران ناموفق بود.");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timeout); };
  }, [userSearch]);

  const toggleUser = (user) => {
    if (!selected.roles.includes(user.role)) {
      warningMessage("ابتدا نقش این کاربر را در کمپین انتخاب کنید");
      return;
    }
    setUsersInfo((previous) => ({ ...previous, [user._id]: user }));
    update({ users: selected.users.includes(user._id)
      ? selected.users.filter((id) => id !== user._id)
      : [...selected.users, user._id] });
  };

  const save = async (campaign) => {
    if (loading || loadFailed || busyId || !campaign) return;
    const validationError = validateCampaign(campaign);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError("");
    setBusyId(campaign.id);
    try {
      const body = { version, campaign: {
        ...campaign,
        minimumOrderPrice: Number(campaign.minimumOrderPrice),
        discountValue: Number(campaign.discountValue),
      } };
      const isPersisted = persistedIds.includes(campaign.id);
      const response = isPersisted
        ? await putApi({ path: `admin/campaigns/${encodeURIComponent(campaign.id)}`, body })
        : await postApi({ path: "admin/campaigns", body });
      const result = response?.data?.result;
      const savedCampaign = result?.campaign;
      if (!savedCampaign || !Number.isInteger(result?.version)) {
        throw new Error("پاسخ ذخیره کمپین معتبر نیست");
      }
      setVersion(result.version);
      setCampaigns((list) => list.map((item) => item.id === campaign.id ? savedCampaign : item));
      setPersistedIds((ids) => ids.includes(savedCampaign.id) ? ids : [...ids, savedCampaign.id]);
      setDirtyIds((ids) => ids.filter((id) => id !== campaign.id));
      if (selectedId === campaign.id && savedCampaign.id !== campaign.id) {
        selectCampaign(savedCampaign.id);
      }
      window.dispatchEvent(new Event("campaigns-updated"));
      successMessage("کمپین ذخیره شد");
    } catch (error) {
      const message = getApiError(error, "ذخیره کمپین انجام نشد.");
      setFormError(message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <Title title="کمپین‌ها" />
      <p className="my-4 text-sm text-gray-600 dark:text-gray-300">
        کمپین را بسازید، شرط‌ها را تنظیم کنید و ذخیره بزنید.
      </p>
      <div className="grid items-start gap-6 lg:grid-cols-[300px_minmax(0,1fr)]">
        <nav className="rounded-xl border border-gray-200 p-3 dark:border-gray-700" aria-label="فهرست کمپین‌ها">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h2 className="font-semibold text-gray-800 dark:text-white">فهرست کمپین‌ها <span className="text-sm font-normal text-gray-500">({campaigns.length})</span></h2>
            <button type="button" onClick={add} disabled={loading || loadFailed || !!busyId} className="rounded-lg bg-indigo-600 px-3 py-2 text-sm text-white hover:bg-indigo-700 disabled:opacity-50">+ کمپین جدید</button>
          </div>
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">فعال‌ها بالای فهرست‌اند.</p>
          {loading && <p className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500 dark:bg-gray-800">در حال دریافت کمپین‌ها...</p>}
          {loadFailed && <p className="rounded-lg bg-red-50 p-4 text-center text-sm text-red-600 dark:bg-red-950">دریافت کمپین‌ها ناموفق بود.</p>}
          {!loading && !loadFailed && !campaigns.length && <p className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-500 dark:bg-gray-800">هنوز کمپینی ثبت نشده است.</p>}
          <div className="space-y-2">
            {listedCampaigns.map((campaign) => (
              <div key={campaign.id} className={`flex items-stretch gap-1 rounded-lg border p-1 ${selectedId === campaign.id ? "border-indigo-400 bg-indigo-50 dark:bg-indigo-950" : "border-gray-200 dark:border-gray-700"}`}>
                <button type="button" onClick={() => selectCampaign(campaign.id)} aria-current={selectedId === campaign.id ? "true" : undefined}
                  className="min-w-0 flex-1 rounded-md p-2 text-right hover:bg-gray-100 dark:hover:bg-gray-800">
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium text-gray-800 dark:text-white" title={campaign.name}>{campaign.name || "بدون نام"}</span>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${campaign.isActive ? "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300" : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}>{campaign.isActive ? "فعال" : "غیرفعال"}</span>
                  </span>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">شروع: {formatCampaignDate(campaign.startsAt)}</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">پایان: {formatCampaignDate(campaign.endsAt)}</span>
                  {dirtyIds.includes(campaign.id) && <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">{persistedIds.includes(campaign.id) ? "تغییرات ذخیره‌نشده" : "کمپین ذخیره‌نشده"}</span>}
                </button>
                <button type="button" onClick={() => remove(campaign)} disabled={!!busyId} aria-label={`حذف کمپین ${campaign.name}`}
                  title="حذف کمپین" className="self-center rounded-lg px-2 py-2 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950">حذف</button>
              </div>
            ))}
          </div>
        </nav>
        {selected ? (
          <fieldset disabled={!!busyId} className="grid min-w-0 gap-5 rounded-xl border p-5 md:grid-cols-2 dark:border-gray-700 dark:text-white disabled:opacity-70">
            <div className="md:col-span-2 border-b pb-4 dark:border-gray-700">
              <h2 className="text-lg font-semibold">{selected.name || "کمپین جدید"}</h2>
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>نام کمپین</label>
              <input className={fieldClass} value={selected.name} onChange={(event) => update({ name: event.target.value })} />
              <Info>این نام را در فهرست کمپین‌ها می‌بینید.</Info>
            </div>
            <CampaignDateTime label="شروع کمپین؛ تاریخ و ساعت" value={selected.startsAt} onChange={(startsAt) => update({ startsAt })} />
            <CampaignDateTime label="پایان کمپین؛ تاریخ و ساعت" value={selected.endsAt} onChange={(endsAt) => update({ endsAt })} />
            <div className="md:col-span-2">
              <span className={labelClass}>چه نقش‌هایی شامل شوند؟</span>
              <div className="flex flex-wrap gap-4">
                {roleOptions.map((role) => <label key={role.value} className="flex items-center gap-2">
                  <input type="checkbox" checked={selected.roles.includes(role.value)} onChange={() => toggleRole(role.value)} />{role.label}
                </label>)}
              </div>
              <Info>فقط نقش‌های انتخاب‌شده می‌توانند از این تخفیف استفاده کنند.</Info>
            </div>
            <div className="md:col-span-2 rounded-lg border p-4">
              <label className="flex items-center gap-2 mb-3">
                <input type="checkbox" checked={!selected.users.length} onChange={() => update({ users: [] })} />
                همه کاربرانِ نقش‌های مجاز
              </label>
              <Info>اگر کاربری انتخاب نکنید، همه افرادِ نقش‌های بالا شامل می‌شوند.</Info>
              <input className={fieldClass} type="search" placeholder="نام یا شماره موبایل" value={userSearch} onChange={(event) => setUserSearch(event.target.value)} />
              <Info>برای محدودکردن کمپین، نام یا موبایل را جستجو کنید؛ مثل ^0912.</Info>
              {searching && <p className="mt-2 text-sm text-gray-500" role="status">در حال جستجو...</p>}
              {searchError && <p className="mt-2 text-sm text-red-600" role="alert">{searchError}</p>}
              {!!userSearch.trim() && !searching && !searchError && !searchResults.length && <p className="mt-2 text-sm text-gray-500">کاربری پیدا نشد.</p>}
              {!!searchResults.length && <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg">
                {searchResults.map((user) => <button key={user._id} type="button" onClick={() => toggleUser(user)}
                  disabled={!selected.roles.includes(user.role)}
                  title={!selected.roles.includes(user.role) ? "نقش این کاربر در کمپین مجاز نیست" : undefined}
                  className="block w-full border-b p-2 text-right hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-gray-800">
                  {selected.users.includes(user._id) ? "✓ " : "+ "}{user.name} — {user.mobile} ({roleOptions.find((role) => role.value === user.role)?.label || user.role})
                </button>)}
              </div>}
              {!!selected.users.length && <div className="mt-3 flex flex-wrap gap-2">
                {selected.users.map((id) => <button key={id} type="button" onClick={() => update({ users: selected.users.filter((user) => user !== id) })}
                  className="rounded-full bg-indigo-100 px-3 py-1 text-sm text-indigo-900">
                  {usersInfo[id]?.name || usersInfo[id]?.mobile || id} ×
                </button>)}
              </div>}
            </div>
            <div>
              <label className={labelClass}>حداقل مبلغ خرید</label>
              <input className={fieldClass} type="number" min="0" step="1" value={selected.minimumOrderPrice}
                onChange={(event) => update({ minimumOrderPrice: event.target.value })} />
              <Info>خرید باید به این مبلغ برسد؛ هزینه ارسال حساب نمی‌شود.</Info>
            </div>
            <div>
              <label className={labelClass}>مبلغ خرید قبل یا بعد از تخفیف کالا؟</label>
              <select className={fieldClass} value={selected.productPriceBasis} onChange={(event) => update({ productPriceBasis: event.target.value })}>
                <option value="before">قبل از تخفیف کالا</option>
                <option value="after">بعد از تخفیف کالا</option>
              </select>
              <Info>{selected.productPriceBasis === "before" ? "حداقل خرید با قیمت اصلی کالاها بررسی می‌شود." : "حداقل خرید با قیمت تخفیف‌خوردهٔ کالاها بررسی می‌شود."}</Info>
            </div>
            <div>
              <label className={labelClass}>مبلغ خرید قبل یا بعد از تخفیف نماینده؟</label>
              <select className={fieldClass} value={selected.affiliatePriceBasis} onChange={(event) => update({ affiliatePriceBasis: event.target.value })}>
                <option value="before">قبل از تخفیف نماینده</option>
                <option value="after">بعد از تخفیف نماینده</option>
              </select>
              <Info>{selected.affiliatePriceBasis === "before" ? "تخفیف خرید نماینده روی شرط حداقل اثر ندارد." : selected.affiliateDiscountOrder === "before" ? "مبلغ بعد از تخفیف نماینده و کمپین برای شرط حداقل بررسی می‌شود." : "مبلغ بعد از تخفیف خرید نماینده برای شرط حداقل بررسی می‌شود."}</Info>
            </div>
            <div>
              <label className={labelClass}>تخفیف کمپین چه زمانی کم شود؟</label>
              <select className={fieldClass} value={selected.affiliateDiscountOrder} onChange={(event) => update({ affiliateDiscountOrder: event.target.value })}>
                <option value="before">قبل از تخفیف نماینده</option>
                <option value="after">بعد از تخفیف نماینده</option>
              </select>
              <Info>{selected.affiliateDiscountOrder === "before" ? "اول تخفیف کمپین کم می‌شود، بعد تخفیف خرید نماینده." : "اول تخفیف خرید نماینده کم می‌شود، بعد تخفیف کمپین."}</Info>
            </div>
            <div>
              <label className={labelClass}>نوع تخفیف</label>
              <select className={fieldClass} value={selected.discountType} onChange={(event) => update({ discountType: event.target.value })}>
                <option value="percent">درصد</option>
                <option value="amount">مبلغ ثابت</option>
              </select>
              <Info>تخفیف می‌تواند درصدی یا یک مبلغ ثابت باشد.</Info>
            </div>
            <div>
              <label className={labelClass}>مقدار تخفیف</label>
              <input className={fieldClass} type="number" min="0" max={selected.discountType === "percent" ? 100 : undefined}
                step={selected.discountType === "percent" ? "any" : "1"} value={selected.discountValue}
                onChange={(event) => update({ discountValue: event.target.value })} />
              <Info>{selected.discountType === "percent" ? "درصدی بیشتر از ۰ و حداکثر ۱۰۰ وارد کنید." : "مبلغ را با همان واحد قیمت کالاهای سایت وارد کنید."}</Info>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={selected.isActive} onChange={(event) => update({ isActive: event.target.checked })} />
                کمپین فعال باشد
              </label>
              <Info>اگر خاموش باشد، تخفیف اجرا نمی‌شود.</Info>
            </div>
            <div className="flex flex-wrap items-center gap-3 border-t pt-4 md:col-span-2 dark:border-gray-700">
              <button type="button" disabled={!dirtyIds.includes(selected.id) || !!busyId} onClick={() => save(selected)}
                className="rounded-lg bg-green-600 px-6 py-3 text-white hover:bg-green-700 disabled:opacity-50">
                {busyId === selected.id ? "در حال ذخیره..." : persistedIds.includes(selected.id) ? "ذخیره تغییرات این کمپین" : "ایجاد این کمپین"}
              </button>
              {dirtyIds.includes(selected.id) && <span className="text-sm text-amber-700 dark:text-amber-300">{persistedIds.includes(selected.id) ? "تغییرات این کمپین ذخیره نشده‌اند." : "این کمپین هنوز ایجاد نشده است."}</span>}
            </div>
            {formError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700 md:col-span-2 dark:bg-red-950 dark:text-red-300">{formError}</p>}
          </fieldset>
        ) : <p className="rounded-xl border p-6 text-gray-500">کمپینی انتخاب نشده است.</p>}
      </div>
    </div>
  );
}
